import React, { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useApp } from "../context/AppContext";
import { inr } from "../lib/format";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import AuthModal from "./AuthModal";

const QUICK = [100, 500, 1000, 2500];

// Run-line market — session (per-over) OR lambi (full-innings total). Both share the
// SAME even-money model (rate 100, win profit == stake) and the SAME backend security.
// `type` = "session" | "lambi". Layout: exchange-style group (navy header, No/Yes cells).
export default function SessionMarket({ match, type = "session" }) {
  const { isAuthed, balance, placeSessionBet, sessionBets } = useApp();
  const { toast } = useToast();
  const [side, setSide] = useState(null); // "yes" | "no"
  const [stake, setStake] = useState("");
  const [placing, setPlacing] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const sm = type === "lambi" ? match.lambiMarket : match.sessionMarket;
  if (!sm || !sm.over) return null;

  const isLambi = type === "lambi";
  const label = isLambi ? `Lambi (${sm.over} ov)` : `${sm.over} Over Runs`;
  const bettingClosed = sm.bettingOpen === false;
  const disabled = match.status !== "live" || !!match.bettingSuspended || !!match.sessionSuspended || bettingClosed;
  const stakeNum = Number(stake) || 0;
  const line = side === "yes" ? sm.yesLine : sm.noLine;

  const mine = (sessionBets || []).filter(
    (b) => b.matchId === match.id && b.status === "open" && b.over === sm.over && (b.market || "session") === type
  );
  const sumStake = (s) => mine.filter((b) => b.side === s).reduce((t, b) => t + (Number(b.stake) || 0), 0);
  const noStake = sumStake("no");
  const yesStake = sumStake("yes");

  const pick = (s) => { if (disabled) return; setSide(s); setStake(""); };

  const confirm = async () => {
    if (!isAuthed) { setAuthOpen(true); return; }
    if (!side) { toast({ title: "Pick No or Yes first" }); return; }
    if (stakeNum <= 0) { toast({ title: "Enter a stake amount" }); return; }
    if (stakeNum > balance) { toast({ title: "Insufficient balance", description: `You need ${inr(stakeNum)} to place this bet.` }); return; }
    setPlacing(true);
    const res = await placeSessionBet({ matchId: match.id, side, stake: stakeNum, market: type });
    setPlacing(false);
    if (res.ok) {
      toast({ title: `${isLambi ? "Lambi" : "Session"} bet placed ✅ · Ref ${res.ref || ""}`.trim(), description: `${side === "yes" ? "Yes" : "No"} ${res.line} · ${sm.name}` });
      setSide(null); setStake("");
    } else {
      toast({ title: "Could not place bet", description: res.error });
    }
  };

  const cell = (s, value, myStake) => {
    const active = side === s;
    const tone = sm.held || disabled ? "bg-zinc-300 text-zinc-500" : s === "no"
      ? (active ? "bg-[#F77FAE] ring-2 ring-inset ring-rose-700" : "bg-[#F994BA] hover:bg-[#F77FAE]")
      : (active ? "bg-[#5AAEEA] ring-2 ring-inset ring-sky-800" : "bg-[#72BBEF] hover:bg-[#5AAEEA]");
    return (
      <button data-testid={`${type}-${s}-btn`} onClick={() => pick(s)} disabled={disabled}
        className={`h-[46px] flex flex-col items-center justify-center leading-none transition-colors disabled:cursor-not-allowed ${tone}`}>
        <span className="font-display font-extrabold text-[17px] text-slate-900">{value}</span>
        <span className="text-[10px] text-slate-800/80 mt-0.5">100</span>
        {myStake > 0 && <span data-testid={`${type}-${s}-stake`} className="sr-only">{inr(myStake)}</span>}
      </button>
    );
  };

  return (
    <div className="rounded-[3px] overflow-hidden shadow-sm" data-testid={`market-${type}`}>
      <button onClick={() => setCollapsed((v) => !v)} className="w-full flex items-center justify-between bg-[#1f3b5b] text-white px-3 py-2 text-left">
        <span className="font-semibold text-[13px]">{label}</span>
        <span className="flex items-center gap-2">
          {!isLambi && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-white/15 px-1.5 py-0.5 text-[11px] font-semibold" data-testid={`${type}-progress`}>
              R×B <span data-testid={`${type}-runs-badge`} className="text-rose-200">{sm.sessionRun ?? 0}</span>/<span data-testid={`${type}-balls-badge`} className="text-sky-200">{sm.sessionBall ?? 0}</span>
            </span>
          )}
          {bettingClosed && <span className="text-[10px] font-bold uppercase text-amber-300">Suspended</span>}
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </span>
      </button>
      {!collapsed && (
        <div className="bg-white">
          <div className="grid grid-cols-[1fr_84px_1fr_84px] border-b border-slate-200">
            <div className="flex flex-col justify-center px-3 py-2 text-[13px] text-slate-800 border-r border-slate-200">
              <span className="font-medium">No</span>
              {noStake > 0 && <span className="text-[11px] font-semibold text-rose-600">{inr(noStake)}</span>}
            </div>
            {cell("no", sm.noLine, noStake)}
            <div className="flex flex-col justify-center px-3 py-2 text-[13px] text-slate-800 border-x border-slate-200">
              <span className="font-medium">Yes</span>
              {yesStake > 0 && <span className="text-[11px] font-semibold text-sky-700">{inr(yesStake)}</span>}
            </div>
            {cell("yes", sm.yesLine, yesStake)}
          </div>
          {bettingClosed && sm.closeMsg && <div className="px-3 py-1.5 text-[11px] text-amber-700 bg-amber-50">{sm.closeMsg}</div>}

          {side && !disabled && (
            <div className="p-2.5 space-y-2 bg-slate-50 border-t border-slate-200" data-testid={`${type}-slip`}>
              <div className="flex items-center justify-between text-sm text-slate-800">
                <span className="font-bold flex items-center gap-1.5">
                  <span className={`text-[10px] font-extrabold uppercase rounded px-1.5 py-0.5 text-slate-900 ${side === "yes" ? "bg-[#72BBEF]" : "bg-[#F994BA]"}`}>{side === "yes" ? "Yes" : "No"}</span>
                  <span>{line}</span>
                </span>
                <span className="text-[11px] text-slate-500">{side === "yes" ? `Wins if \u2265 ${line}` : `Wins only if < ${line}`}</span>
              </div>
              <Input type="number" inputMode="numeric" min={0} value={stake} onChange={(e) => setStake(e.target.value)} placeholder="Enter stake"
                className="h-10 text-base bg-white text-slate-900 border-slate-300" data-testid={`${type}-stake-input`} />
              <div className="grid grid-cols-4 gap-1.5">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => setStake(String((Number(stake) || 0) + q))} className="rounded-sm bg-[#1f3b5b] text-white py-1.5 text-xs font-semibold active:scale-95 transition-transform" data-testid={`${type}-quick-${q}`}>+{q}</button>
                ))}
              </div>
              <div className="flex justify-between text-sm border-t border-slate-200 pt-1.5 text-slate-700">
                <span>Return on win</span>
                <span className="font-display font-bold text-emerald-700" data-testid={`${type}-return`}>{inr(Math.round(stakeNum * 2))}</span>
              </div>
              <Button className="w-full h-10 text-sm font-bold text-black border-0 bg-[#f5b400] hover:bg-[#ffc21a]" onClick={confirm} disabled={placing} data-testid={`${type}-confirm`}>
                {isAuthed ? (placing ? "Placing…" : `Place Bet · ${inr(Math.round(stakeNum))}`) : "Login to bet"}
              </Button>
            </div>
          )}
        </div>
      )}
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
